import {
  pointerKind as detectPointerKind,
  type PointerKind as PkgPointerKind,
} from '@bn-games/input-core';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../utils/constants.js';

export type InteractionKind = 'down' | 'move' | 'up' | 'leave' | 'cancel';
export type PointerKind = PkgPointerKind;

/**
 * Domino pointer event in canvas-internal coords (the 1280×720 logical
 * coordinate system, before any DPR/display scaling). All pointers are
 * forwarded — multi-touch is needed for pinch-to-zoom. The dispatcher
 * upstream is responsible for filtering by `pointerId` and `isPrimary`.
 */
export interface PointerInteraction {
  readonly kind: InteractionKind;
  readonly canvasX: number;
  readonly canvasY: number;
  readonly pointer: PointerKind;
  readonly pointerId: number;
  readonly isPrimary: boolean;
}

export interface WheelInteraction {
  readonly canvasX: number;
  readonly canvasY: number;
  /** Sign-of-deltaY normalized to +1 (zoom out) or -1 (zoom in). */
  readonly deltaSign: 1 | -1;
}

export interface InputHandlerOptions {
  readonly canvas: HTMLCanvasElement;
  readonly onInteraction: (event: PointerInteraction) => void;
  readonly onWheel?: (event: WheelInteraction) => void;
  /**
   * Returns true when `applyCanvasLayout` is layering a 90° CW rotation on
   * top of the base scale (portrait phones/tablets). Pointer events arrive
   * in the rotated CSS space, so we inverse-rotate into the unrotated
   * CANVAS_WIDTH × CANVAS_HEIGHT renderer coords.
   */
  readonly isRotated?: () => boolean;
}

export class InputHandler {
  private readonly disposers: Array<() => void> = [];

  constructor(private readonly opts: InputHandlerOptions) {
    this.attach();
  }

  private attach(): void {
    const { canvas } = this.opts;

    const toRendererCoords = (
      eventX: number,
      eventY: number,
    ): { x: number; y: number } => {
      const rect = canvas.getBoundingClientRect();
      const relX = (eventX - rect.left) / rect.width;
      const relY = (eventY - rect.top) / rect.height;
      if (this.opts.isRotated?.()) {
        // Inverse of the 90° CW transform applied in applyCanvasLayout:
        // event x in CSS-rotated space → renderer Y (inverted),
        // event y → renderer X.
        return {
          x: relY * CANVAS_WIDTH,
          y: (1 - relX) * CANVAS_HEIGHT,
        };
      }
      return { x: relX * CANVAS_WIDTH, y: relY * CANVAS_HEIGHT };
    };

    const dispatch = (kind: InteractionKind, e: PointerEvent): void => {
      const { x, y } = toRendererCoords(e.clientX, e.clientY);
      this.opts.onInteraction({
        kind,
        canvasX: x,
        canvasY: y,
        pointer: detectPointerKind(e),
        pointerId: e.pointerId,
        isPrimary: e.isPrimary,
      });
    };

    const onDown = (e: PointerEvent): void => {
      dispatch('down', e);
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Some platforms throw if already captured; safe to ignore.
      }
    };
    const onMove = (e: PointerEvent): void => dispatch('move', e);
    const onUp = (e: PointerEvent): void => {
      dispatch('up', e);
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Already released — ignore.
      }
    };
    const onCancel = (e: PointerEvent): void => dispatch('cancel', e);
    const onLeave = (e: PointerEvent): void => dispatch('leave', e);
    const onDragStart = (e: DragEvent): void => e.preventDefault();
    const onWheel = (e: WheelEvent): void => {
      if (!this.opts.onWheel) return;
      e.preventDefault();
      const { x, y } = toRendererCoords(e.clientX, e.clientY);
      this.opts.onWheel({
        canvasX: x,
        canvasY: y,
        deltaSign: e.deltaY > 0 ? 1 : -1,
      });
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('dragstart', onDragStart);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    this.disposers.push(
      () => canvas.removeEventListener('pointerdown', onDown),
      () => canvas.removeEventListener('pointermove', onMove),
      () => canvas.removeEventListener('pointerup', onUp),
      () => canvas.removeEventListener('pointercancel', onCancel),
      () => canvas.removeEventListener('pointerleave', onLeave),
      () => canvas.removeEventListener('dragstart', onDragStart),
      () => canvas.removeEventListener('wheel', onWheel),
    );
  }

  dispose(): void {
    for (const fn of this.disposers) fn();
    this.disposers.length = 0;
  }
}
